<template>
  <v-timeline-item
    v-if="!do_not_show"
    class="timeline-item-animated"
    :style="{ 'animation-delay': (index * 0.05) + 's' }"
    :icon="location.anomaly ? 'fas fa-exclamation-triangle' : (location.items.length !== 0 || index === full_locations.length - 1 || index === 0 ? 'fas fa-map-marker-alt' : stats.icon)"
    :color="location.anomaly ? 'error' : (location.items.length !== 0 || index === full_locations.length - 1 || index === 0 ? 'primary' : 'grey')"
    fill-dot
    small
  >
    <v-card v-if="location.anomaly || location.items.length >= 1 || index === full_locations.length - 1 || index === 0" @click="details_open = !details_open" class="elevation-2 timeline-card" style="cursor: pointer;" :class="{'red lighten-5': location.anomaly}">
      <v-card-title class="text-subtitle-1 font-weight-bold pb-1 pt-3 d-flex justify-space-between align-center">
        <span class="primary--text">{{ getLocationTime(location) }}</span>
        <div>
          <v-btn v-if="editMode" icon color="primary" @click.stop="$emit('edit', location)">
            <i class="fal fa-edit"></i>
          </v-btn>
          <v-btn v-if="editMode" icon color="error" @click.stop="$emit('delete', location)">
            <i class="fal fa-trash"></i>
          </v-btn>
          <i :class="!details_open ? 'fad fa-chevron-down' : 'fad fa-chevron-up'" class="grey--text text--darken-1 ml-2"></i>
        </div>
      </v-card-title>
      <v-card-text class="pb-3">
        <div class="text-body-1 font-weight-medium grey--text text--darken-3">{{ location.data.address }}</div>
        <v-expand-transition>
          <div class="point_details mt-3" v-if="details_open">
            <div class="text-caption grey--text mb-2">
              <i class="fas fa-map-marker-alt mr-1"></i> {{ location.data.lat }}, {{ location.data.lon }}
            </div>
            <v-chip small color="primary" outlined v-if="location.items && location.items.length > 0" class="mr-2 mb-2 font-weight-medium">
              <i class="fas fa-layer-group mr-2"></i> {{ $t("today.total_locations_at_this_point") }}: {{ location.items.length }}
            </v-chip>
            <div v-if="location.anomaly" class="error--text mt-2 font-weight-medium pa-2 rounded-lg" style="background-color: rgba(255,82,82,0.1);">
              <i class="fas fa-exclamation-circle mr-1"></i>
              Anomaly detected! This point is completely somewhere else. We suggest deleting it.
            </div>
          </div>
        </v-expand-transition>
      </v-card-text>
    </v-card>

    <v-card v-else class="elevation-0 transparent-card mx-2 mt-2">
      <v-card-text class="pa-2 d-flex align-center justify-center">
        <v-chip small class="font-weight-medium" color="grey lighten-4" text-color="grey darken-2">
          <i :class="stats.icon + ' mr-2'"></i>
          {{ moment("2015-01-01").startOf('day')
            .seconds(stats.duration)
            .format('HH:mm') }}h
          <span class="ml-1" v-if="stats.distance > 0">
            ({{ (stats.distance / 1000).toFixed(1) }} km)
          </span>
        </v-chip>
        <span class="grey--text text--darken-1 ml-3 font-weight-medium text-caption text-uppercase tracking-wide">
          {{ $t("timeline.on_the_way") }}
        </span>
      </v-card-text>
    </v-card>
  </v-timeline-item>
</template>

<script>
export default {
  props: {
    location: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    full_locations: {
      type: Array,
      required: true
    },
    editMode: {
      type: Boolean,
      default: false
    },
    isRange: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      details_open: false,
      do_not_show: false,
      stats: {
        duration: 0,
        distance: 0,
        speed: 0,
        type: 'walk',
        icon: 'fas fa-walking'
      }
    };
  },
  mounted() {
    if (this.location.items.length === 0 && this.index !== this.full_locations.length - 1 && this.index !== 0) {
      this.calculateStats();
    }
  },
  methods: {
    parseTime(timestamp, hideDate = false) {
      var date = new Date(parseInt(timestamp) * 1000);
      var timeStr = (date.getHours() + "").padStart(2, "0") +
        ":" +
        (date.getMinutes() + "").padStart(2, "0");
        
      if (this.isRange && !hideDate) {
        var months = [
          this.$t("general.month.january") || "January",
          this.$t("general.month.february") || "February",
          this.$t("general.month.march") || "March",
          this.$t("general.month.april") || "April",
          this.$t("general.month.may") || "May",
          this.$t("general.month.june") || "June",
          this.$t("general.month.july") || "July",
          this.$t("general.month.august") || "August",
          this.$t("general.month.september") || "September",
          this.$t("general.month.october") || "October",
          this.$t("general.month.november") || "November",
          this.$t("general.month.december") || "December",
        ];
        var dateStr = date.getDate() + ". " + months[date.getMonth()];
        return dateStr + ", " + timeStr;
      }
      return timeStr;
    },
    getLocationTime(location) {
      var startDate = new Date(parseInt(location.data.time) * 1000);
      var start_time = this.parseTime(location.data.time);
      var end_time;
      if (location.items.length > 0) {
        let last_point = location.items[location.items.length - 1];
        let endDate = new Date(parseInt(last_point.data.time) * 1000);
        
        let isSameDay = startDate.getFullYear() === endDate.getFullYear() && 
                        startDate.getMonth() === endDate.getMonth() && 
                        startDate.getDate() === endDate.getDate();
                        
        end_time = this.parseTime(last_point.data.time, isSameDay);
      }

      if (end_time !== undefined) {
        return start_time + " - " + end_time;
      }
      return start_time;
    },

    getPreviousPoint(step) {
      if (step === undefined) {
        step = 0;
      }
      return this.full_locations[this.index <= 0 ? 0 : this.index - 1 - step];
    },

    getNextPoint(step) {
      if (step === undefined) {
        step = 0;
      }
      return this.full_locations[
        this.index >= this.full_locations.length
          ? this.full_locations.length - 1
          : this.index + 1 + step
      ];
    },

    getDistance(lat1, lon1, lat2, lon2) {
      const R = 6371e3; // metres
      const φ1 = (lat1 * Math.PI) / 180;
      const φ2 = (lat2 * Math.PI) / 180;
      const Δφ = ((lat2 - lat1) * Math.PI) / 180;
      const Δλ = ((lon2 - lon1) * Math.PI) / 180;

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return R * c;
    },

    calculateStats() {
      var next_step = 0;
      var prev_step = 0;
      while (this.getNextPoint(next_step) && this.getNextPoint(next_step).items && this.getNextPoint(next_step).items.length === 0) {
        if (this.index + 1 + next_step >= this.full_locations.length - 1) {
          break;
        }
        next_step++;
        if (next_step > 200) {
          break;
        }
      }

      while (this.getPreviousPoint(prev_step) && this.getPreviousPoint(prev_step).items && this.getPreviousPoint(prev_step).items.length === 0) {
        if (this.index - 1 - prev_step <= 0) {
          break;
        }
        prev_step++;
        if (prev_step > 200) {
          break;
        }
      }

      if (prev_step > 0) {
        this.do_not_show = true;
        return;
      }

      var prevPointGroup = this.getPreviousPoint(prev_step);
      if (!prevPointGroup) return;
      var last_previous_point = (prevPointGroup.items && prevPointGroup.items.length > 0) ? prevPointGroup.items[prevPointGroup.items.length - 1] : prevPointGroup;
      var first_next_point = this.getNextPoint(next_step);

      if (!last_previous_point || !first_next_point) return;

      var first_time = first_next_point.data.time;
      var last_time = last_previous_point.data.time;
      var duration = first_time - last_time;

      var totalDistance = 0;
      var currentPoint = last_previous_point;
      
      for (let i = this.index - prev_step; i < this.index + next_step; i++) {
        let wp = this.full_locations[i];
        if (wp && currentPoint) {
          totalDistance += this.getDistance(
            currentPoint.data.lat, currentPoint.data.lon,
            wp.data.lat, wp.data.lon
          );
          currentPoint = wp;
        }
      }
      
      if (currentPoint && first_next_point) {
        totalDistance += this.getDistance(
            currentPoint.data.lat, currentPoint.data.lon,
            first_next_point.data.lat, first_next_point.data.lon
        );
      }

      var speed = 0;
      if (duration > 0) {
        speed = (totalDistance / 1000) / (duration / 3600);
      }
      
      var type = "walk";
      var icon = "fas fa-route";
      if (speed > 10) {
        type = "drive";
        icon = "fas fa-route";
      }

      this.stats = {
        duration: duration,
        distance: totalDistance,
        speed: speed,
        type: type,
        icon: icon
      };
    }
  }
};
</script>

<style scoped>
.point_details {
  padding-top: 10px;
}

.timeline-card {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-radius: 16px !important;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.timeline-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
}

.transparent-card {
  border-radius: 12px !important;
  background-color: transparent !important;
  border: 1px dashed rgba(0,0,0,0.1);
}

.tracking-wide {
  letter-spacing: 0.05em;
}

@keyframes slideUpFade {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.timeline-item-animated {
  animation: slideUpFade 0.6s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}
</style>